import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EscalationRuleModal = ({ rule, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    alert_type: '',
    priority: 'medium',
    target_roles: [],
    message_template: '',
    send_sms: true,
    send_email: false,
    send_push: false,
    is_active: true,
    escalation_delay_minutes: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (rule) {
      setFormData({
        alert_type: rule?.alert_type || '',
        priority: rule?.priority || 'medium',
        target_roles: rule?.target_roles || [],
        message_template: rule?.message_template || '',
        send_sms: rule?.send_sms ?? true,
        send_email: rule?.send_email ?? false,
        send_push: rule?.send_push ?? false,
        is_active: rule?.is_active ?? true,
        escalation_delay_minutes: rule?.escalation_delay_minutes || 30
      });
    }
  }, [rule]);

  const alertTypes = [
    { value: 'payment_failure', label: 'Payment Failure', icon: 'CreditCard' },
    { value: 'equipment_issue', label: 'Equipment Issue', icon: 'Wrench' },
    { value: 'capacity_warning', label: 'Capacity Warning', icon: 'Users' },
    { value: 'safety_alert', label: 'Safety Alert', icon: 'Shield' },
    { value: 'maintenance_required', label: 'Maintenance Required', icon: 'Tool' }
  ];

  const priorities = [
    { value: 'critical', label: 'Critical', color: 'error' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'medium', label: 'Medium', color: 'primary' },
    { value: 'low', label: 'Low', color: 'success' }
  ];

  const roles = [
    { value: 'staff', label: 'Staff' },
    { value: 'trainer', label: 'Trainer' },
    { value: 'manager', label: 'Manager' },
    { value: 'owner', label: 'Owner' }
  ];

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev?.target_roles?.includes(role)
        ? prev?.target_roles?.filter(r => r !== role)
        : [...prev?.target_roles, role]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.alert_type) {
      setError('Please select an alert type');
      return;
    }

    if (formData?.target_roles?.length === 0) {
      setError('Please select at least one target role');
      return;
    }

    if (!formData?.message_template?.trim()) {
      setError('Please enter a message template');
      return;
    }

    setLoading(true);
    await onSave(formData);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {rule ? 'Edit Escalation Rule' : 'Create Escalation Rule'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Alert Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Alert Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {alertTypes?.map((type) => (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, alert_type: type?.value }))}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData?.alert_type === type?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name={type?.icon} size={18} />
                  <span className="text-sm font-medium">{type?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Priority Level *
            </label>
            <div className="grid grid-cols-4 gap-3">
              {priorities?.map((priority) => (
                <button
                  key={priority?.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, priority: priority?.value }))}
                  className={`px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    formData?.priority === priority?.value
                      ? `border-${priority?.color} bg-${priority?.color}/10 text-${priority?.color}`
                      : 'border-border bg-background text-foreground hover:border-primary/50'
                  }`}
                >
                  {priority?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Target Roles */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Target Roles * (Select who receives this alert)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {roles?.map((role) => (
                <button
                  key={role?.value}
                  type="button"
                  onClick={() => handleRoleToggle(role?.value)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    formData?.target_roles?.includes(role?.value)
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon
                    name={formData?.target_roles?.includes(role?.value) ? 'CheckSquare' : 'Square'}
                    size={18}
                  />
                  <span className="text-sm font-medium">{role?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Message Template */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Message Template *
            </label>
            <textarea
              value={formData?.message_template}
              onChange={(e) => setFormData(prev => ({ ...prev, message_template: e?.target?.value }))}
              placeholder="Enter message template with variables like {member_name}, {location}, {equipment}..."
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.message_template?.length}/500 characters
            </p>
          </div>

          {/* Escalation Delay */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Escalation Delay (minutes)
            </label>
            <input
              type="number"
              value={formData?.escalation_delay_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, escalation_delay_minutes: parseInt(e?.target?.value) || 30 }))}
              min="5"
              max="120"
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Notification Channels */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Notification Channels
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.send_sms}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_sms: e?.target?.checked }))}
                  className="w-4 h-4"
                />
                <Icon name="MessageSquare" size={18} />
                <span className="text-sm font-medium text-foreground">SMS Notification</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.send_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_email: e?.target?.checked }))}
                  className="w-4 h-4"
                />
                <Icon name="Mail" size={18} />
                <span className="text-sm font-medium text-foreground">Email Notification</span>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.send_push}
                  onChange={(e) => setFormData(prev => ({ ...prev, send_push: e?.target?.checked }))}
                  className="w-4 h-4"
                />
                <Icon name="Bell" size={18} />
                <span className="text-sm font-medium text-foreground">Push Notification</span>
              </label>
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
                className="w-4 h-4"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Active Rule</span>
                <p className="text-xs text-muted-foreground">Enable this escalation rule immediately</p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-muted text-foreground hover:bg-muted/80"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Save'}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EscalationRuleModal;